/**
 * ナビ項目の静的定義のうち、PI Lab の運営・組織の仕組み（改善要望・組織課題・週次MTG）の分。
 * `useNavGroups.tsx` がファイルの行数の上限に当たるため分けている（並び・権限判定は向こうが持つ）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PI Lab の改善要望・組織課題・週次MTG のメニューのキーとアイコン
 * @graph-connects none
 */

import type { ReactNode } from 'react';
import type { PilabNavArea } from '@cortex/core-types';

/**
 * ナビ項目の静的定義（権限判定前のキーとアイコン）。
 *
 * **入口のパスはここに書かない。** `@cortex/core-types` の `PILAB_NAV_AREA_PATHS` が正本で、
 * 改善要望の「どこの話か」もそこから作る。パスを 2 か所に書くと、メニューを足したときに
 * 要望の対象一覧に出てこない（出せない画面ができる）。
 *
 * `key` が `PilabNavArea` なので、**新しいメニューは先に共有テーブルへ足さないと型が通らない**。
 * 足せば要望の対象にも自動で並ぶ。
 */
export interface NavDef {
  key: PilabNavArea;
  icon: ReactNode;
}

/** 改善要望。PI Lab を使う全員が出す入口なので全社員に開放。 @graph-connects none */
export const improvementsNav: NavDef = {
  key: 'improvements',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  ),
};

/**
 * 組織課題（看板）。画面は全員に開き、どのボードが見えるかは API がボードの公開範囲で決める。
 * @graph-connects none
 */
export const orgIssuesNav: NavDef = {
  key: 'orgIssues',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
      />
    </svg>
  ),
};

/**
 * チーム週次MTG。画面は全員に開き、どのシリーズが見えるかは API がシリーズの公開範囲で決める。
 * @graph-connects none
 */
export const teamMeetingsNav: NavDef = {
  key: 'teamMeetings',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
      />
    </svg>
  ),
};
